import { webApi } from '@/shared/api/WebApi';
import type {
  FactoryReleasesRes,
  FactoryBuildsRes,
  FactorySubmissionsRes,
  FactoryTasksRes,
  FactoryDevicesRes,
  FactoryCampaignsRes,
  FactoryTargetsRes,
  FactoryAssetSetsRes,
  FactoryAssetSetCreateReq,
  FactoryAssetSetCreateRes,
  FactoryAssetSet,
  SubmissionDownloads,
} from '@/lib/types/AppFactory';

/**
 * App factory proxy routes (app/factory/*). The api service forwards these to
 * the factory daemon; when the gateway is disabled the routes 404 — callers
 * treat any error as "factory status unavailable" and degrade quietly.
 */
export class AppFactoryApi {
  // ── Customer surface ──

  static async getReleases(cId: number): Promise<FactoryReleasesRes> {
    return webApi.get<FactoryReleasesRes>('app/factory/releases', { cId });
  }

  static async getTasks(cId: number): Promise<FactoryTasksRes> {
    return webApi.get<FactoryTasksRes>('app/factory/tasks', { cId });
  }

  static async getBuilds(cId: number, appCfgId: number): Promise<FactoryBuildsRes> {
    return webApi.get<FactoryBuildsRes>(`app/factory/${appCfgId}/builds`, { cId });
  }

  static async getSubmissions(cId: number, appCfgId: number): Promise<FactorySubmissionsRes> {
    return webApi.get<FactorySubmissionsRes>(`app/factory/${appCfgId}/submissions`, { cId });
  }

  /** Short-lived presigned HTTPS URLs for the submission's pkg + doc (Tier 1). */
  static async getSubmissionDownloads(
    cId: number,
    appCfgId: number,
    submissionId: string,
  ): Promise<SubmissionDownloads> {
    return webApi.get<SubmissionDownloads>(
      `app/factory/${appCfgId}/submissions/${submissionId}/downloads`,
      { cId },
    );
  }

  // ── Brand kit (WS-2.5 asset sets) ──

  static async getAssetSets(cId: number, appCfgId: number): Promise<FactoryAssetSetsRes> {
    return webApi.get<FactoryAssetSetsRes>(`app/factory/${appCfgId}/asset-sets`, { cId });
  }

  static async createAssetSet(
    cId: number,
    appCfgId: number,
    req: FactoryAssetSetCreateReq,
  ): Promise<FactoryAssetSetCreateRes> {
    return webApi.post<FactoryAssetSetCreateRes>(`app/factory/${appCfgId}/asset-set`, { cId }, req);
  }

  static async decideAssetSet(
    cId: number,
    appCfgId: number,
    assetSetId: string,
    state: 'approved' | 'rejected',
  ): Promise<{ assetSet: FactoryAssetSet }> {
    return webApi.post<{ assetSet: FactoryAssetSet }>(
      `app/factory/${appCfgId}/asset-sets/${assetSetId}/decision`,
      { cId },
      { state },
    );
  }

  // ── Operator surface (server-side allowlist; 403 for everyone else) ──

  static async getDevices(cId: number): Promise<FactoryDevicesRes> {
    return webApi.get<FactoryDevicesRes>('app/factory/devices', { cId });
  }

  static async getCampaigns(cId: number): Promise<FactoryCampaignsRes> {
    return webApi.get<FactoryCampaignsRes>('app/factory/campaigns', { cId });
  }

  static async getCampaignTargets(cId: number, campaignId: string): Promise<FactoryTargetsRes> {
    return webApi.get<FactoryTargetsRes>(`app/factory/campaigns/${campaignId}/targets`, { cId });
  }

  static async advanceCampaign(cId: number, campaignId: string): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/campaigns/${campaignId}/advance`, { cId }, {});
  }

  static async abortCampaign(cId: number, campaignId: string): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/campaigns/${campaignId}/abort`, { cId }, {});
  }

  static async transitionSubmission(
    cId: number,
    submissionId: string,
    body: { from: string; to: string; note?: string },
  ): Promise<unknown> {
    return webApi.post<unknown>(`app/factory/submissions/${submissionId}/transition`, { cId }, body);
  }
}
